import { Component, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { amountTypeDict, Constants, frequencyDict, pageInContext, svgIcons, transactionDetailLabels, transactionTypes, transactionTypesDict } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { selectAchInfo, selectAchInfoParty, selectMoneyLinkType, selectStagedStandingAuthid, selectStagedTransactionForm } from '../../../../../../create-request/reducers';
import { AchExtraPrompts, AchTransactionDetails, isAchForm } from '../../../../../../create-request/models/form/TransactionForm';
import { FeatureFlagService } from 'projects/money-movement-ui/src/app/shared/services/feature-flag-service';
import { DatePipe } from '@angular/common';
import { selectAchInstructions, selectEAuthCustomers, selectInfo } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { NamePipe } from 'projects/money-movement-ui/src/app/shared/pipes/NamePipe';
import { selectIsAchPathEnabled } from 'projects/money-movement-ui/src/app/feature-flags/reducers';
import { selectHasAccessInitiateMoneyMovement } from 'projects/money-movement-ui/src/app/authorize/reducers';
import { selectViewInContext } from 'projects/money-movement-ui/src/app/shared/reducers';
import { selectIsStandingAuthViewPage, selectIsTransactionsViewPage, selectViewFrequency } from 'projects/money-movement-ui/src/app/transaction-view/reducers';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-ach-condensed',
  templateUrl: './transaction-details-ach-condensed.component.html',
  styleUrl: './transaction-details-ach-condensed.component.scss'
})
export class TransactionDetailsAchCondensedComponent implements OnInit {
  readonly labels = transactionDetailLabels;
  readonly incoming_svg = svgIcons.achReviewPageIncoming;
  readonly outgoing_svg = svgIcons.achReviewPageOutgoing;
  destroy$: Subject<boolean> = new Subject<boolean>();
  isRecurringSetup: boolean;
  transferDirection: string;
  withdrawalDate: string;
  completionDate: string;
  fundsAvailableToTrade: string;
  contributionReason: string;
  trustIncomePercent: string;
  trustPrincipalPercent: string;
  trustTaxCategory: string;
  trustTaxCode: string;
  trustPaidFor: string;
  trustComment: string;
  fedTaxPercent: string;
  stateTaxPercent: string;
  distributionReason: string;
  amountType: string;
  amount: string;
  frequencyOfOccurrence: string;
  secondCompletionDate: string;
  endDate: string;
  step: string;
  transactionType: string;
  nickname: string;
  isThirdParty: boolean;
  selectedAuthorization: string;
  transactionTypeLabel: string = transactionTypesDict[transactionTypes.ach];
  frequency: string;
  grossAmount: string;
  isIra: boolean;
  isPtrs: boolean;
  netAmount: string;
  isStandingAuthViewPage: boolean = false;
  isTransactionViewPage: boolean = false;
  moneyLinkType: string;
  withdrawalDateCopy: string = Constants.expectedWithdrawalDateCopy;
  fundsAvailableToTradeCopy: string = Constants.fundsAvailableToTrade;
  accountHolderRequestingTransaction: string = '';
  enableAchPath: boolean = false;

  constructor(private store: Store, private datePipe: DatePipe, private namePipe: NamePipe){

  }

  ngOnInit(): void {
    this.store.select(selectIsAchPathEnabled).pipe(takeUntil(this.destroy$)).subscribe(enabled => this.enableAchPath = enabled);

    combineLatest([
      this.store.pipe(select(selectStagedTransactionForm)),
      this.store.pipe(select(selectAchInfo)),
      this.store.select(selectViewFrequency),
      this.store.select(selectEAuthCustomers)
    ]).pipe(takeUntil(this.destroy$),
    ).subscribe(([form, achInfo, frequency, eAuthCustomers]) => {
      if (form?.transactionType && isAchForm(form)) {
        const extraPrompts = form.extraPrompts;
        const transactionDetails = form.transactionDetails;
        const viewFrequency = frequency ?? '';
        this.isRecurringSetup = this.isRecurring(extraPrompts, viewFrequency);
        this.transferDirection = extraPrompts.transferDirection;
        this.withdrawalDate = transactionDetails.withdrawalDate;
        this.completionDate = transactionDetails.completionDate ?? achInfo.processDate;
        this.fundsAvailableToTrade = transactionDetails.fundsAvailableToTrade;
        this.contributionReason = transactionDetails.contributionReason;
        this.trustIncomePercent = transactionDetails.trustIncomePercent ?? '';
        this.trustPrincipalPercent = transactionDetails.trustPrincipalPercent ?? '';
        this.trustTaxCategory = transactionDetails.trustTaxCategory ?? '';
        this.trustTaxCode = transactionDetails.trustTaxCode ?? '';
        this.trustPaidFor = transactionDetails.trustPaidFor ?? '';
        this.trustComment = transactionDetails.trustComment ?? '';
        this.fedTaxPercent = achInfo.fedTaxPercent ?? '';
        this.stateTaxPercent = achInfo.stateTaxPercent ?? '';
        this.grossAmount = achInfo.withdrawalAmount;
        this.netAmount = achInfo.transferAmount;
        this.distributionReason = achInfo.distributionReason ? achInfo.distributionReason :  transactionDetails?.taxWithholdingsFormGroup?.distributionReason ?? '';
        this.amountType = achInfo.amountType;
        if (achInfo.amountType === amountTypeDict.net) {
          this.amount = achInfo.transferAmount;
        } else {
          this.amount = achInfo.withdrawalAmount;
        }
        this.frequency = !this.isRecurringSetup ? frequencyDict.onetime : frequencyDict.recurring;
        this.frequencyOfOccurrence = this.isRecurringSetup ? achInfo.frequency ? achInfo.frequency : achInfo.frequencyList.find(frequency => frequency.code === transactionDetails.recurringFrequency)!.description : '';
        this.secondCompletionDate = this.getSecondCompletionDate(form.transactionDetails, achInfo) ?? '';
        this.endDate = this.getEndDate(this.isRecurringSetup, form.transactionDetails, achInfo) ?? '';

        const accountHolderRequestingTransactionId = extraPrompts?.requestingAccountHolderId ?? '';
        if (this.enableAchPath && !!accountHolderRequestingTransactionId) {
          const accountHolder = eAuthCustomers.find((cust) => cust.customerId === accountHolderRequestingTransactionId);
          if (accountHolder) {
            const name = `${this.namePipe.transform(accountHolder.firstName)} ${this.namePipe.transform(accountHolder.lastName)}`;
            const isDpoa = accountHolder.accountRoles?.includes('DPOA');
            this.accountHolderRequestingTransaction = isDpoa ? `${name} (DPOA)` : name;
          } else {
            this.accountHolderRequestingTransaction = '';
          }
        }

        this.validateDates();
      }
    });

    this.store.select(selectStagedStandingAuthid).pipe(takeUntil(this.destroy$)).subscribe(selectedAuthorization => {
      this.selectedAuthorization = selectedAuthorization?.includes('-') ? (selectedAuthorization?.split('-')[1]) : selectedAuthorization;
    });

    combineLatest([
      this.store.select(selectAchInstructions),
      this.store.select(selectViewInContext),
      this.store.select(selectViewFrequency),
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectAchInfoParty)
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([instructions, step, frequency, hasInitReleaseAccess, achInfoParty]) => {
        if(hasInitReleaseAccess && !instructions?.length)
        {
          this.step = step;
          if (step === pageInContext.view) {
            this.frequencyOfOccurrence = frequency ?? '';
          }
          if (achInfoParty === 'Y') {
            this.transactionType = `${this.transactionTypeLabel} - 3rd party`;
          } else {
            this.transactionType = `${this.transactionTypeLabel} - 1st party`;
          }
        }
        else{
        instructions.forEach((instruction) => {

          this.step = step;
          if (step === pageInContext.view) {
            this.frequencyOfOccurrence = frequency ?? '';
          }

          if (instruction.cashieringRqstId === this.selectedAuthorization) {
            this.nickname = instruction.nickname;
            this.isThirdParty = instruction.isThirdParty;
            if (this.isThirdParty) {
              this.transactionType = `${this.transactionTypeLabel} - 3rd party`;
            } else {
              this.transactionType = `${this.transactionTypeLabel} - 1st party`;
            }
          }
        });
      }
    });

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.isIra = info.classifications.isIra;
      this.isPtrs = info.classifications.isPtrs;
    });

    this.store
      .select(selectIsStandingAuthViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isStandingAuthViewPage => this.isStandingAuthViewPage = isStandingAuthViewPage);

    this.store
      .select(selectIsTransactionsViewPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isTransactionViewPage => this.isTransactionViewPage = isTransactionViewPage);

    this.store
      .select(selectMoneyLinkType)
      .pipe(takeUntil(this.destroy$),
      filter( _ => this.isStandingAuthViewPage || this.isTransactionViewPage))
      .subscribe(moneyLinkType => {
        this.moneyLinkType = moneyLinkType;
        let lastIndex = this.moneyLinkType.lastIndexOf('-')
        this.transactionType = this.transactionTypeLabel + ' ' + this.moneyLinkType.slice(lastIndex)
    });
  }

  validateDates(): void {
    if (this.completionDate?.match("0001-01-01")) {this.completionDate = ''};
    if (this.withdrawalDate?.match("12-31-9999") || this.withdrawalDate?.match("01/01/0001")) {this.withdrawalDate = ''};
  }

  get grossOrNetAmount(): string {
    return this.isIra && this.transferDirection === 'outgoing' ? this.grossAmount : this.amount;
  }

  get pageHeader(): string {
    return this.isStandingAuthViewPage ? 'Standing authorization details' : 'Transaction details';
  }

  isRecurring(extraPrompts: AchExtraPrompts, viewFrequency: string) {
    if (extraPrompts.frequency === 'recurring' || (viewFrequency && viewFrequency != 'On-Request')) {
      return true;
    }
    else {
      return false;
    }
  }

  getSecondCompletionDate(formTransactionDetails: AchTransactionDetails, achInfo: any) {
    if (formTransactionDetails?.recurringFrequency === 'SEMIMTH' || formTransactionDetails?.recurringFrequency?.toLowerCase() === '2 times/month') {
      return this.datePipe.transform(formTransactionDetails?.secondProcessDate!, 'MM/dd/yyyy');
    }
    else if (achInfo?.frequency?.toLowerCase() === '2 times/month') {
      return this.datePipe.transform(achInfo?.secondProcessDate!, 'MM/dd/yyyy');
    }
    else {
      return '';
    }
  }

  getEndDate(isRecurring: boolean, formTransactionDetails: AchTransactionDetails, achInfo: any) {
    if (isRecurring) {
      const endDate = formTransactionDetails?.endDate ?? achInfo?.endDate;
      return endDate ? this.datePipe.transform(endDate, 'MM/dd/yyyy') : '';
    }
    else {
      return '';
    }
  }
}
